// ./src/components/generic/ColorSelector/ColorSelector.tsx
import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Stack,
  Group,
  Text,
  TextInput,
  ColorPicker,
  SegmentedControl,
  NumberInput,
  ColorSwatch,
  Box,
  Slider,
  ActionIcon,
  Checkbox,
} from "@mantine/core";
import { AppIcon } from "../AppIcon";
import { getRandomHexColor } from "@/utils";

const PRESET_COLORS = [
  "#ffffff",
  "#000000",
  "#f8fafc",
  "#1e293b",
  "#334155",
  "#ef4444",
  "#f97316",
  "#f59e0b",
  "#eab308",
  "#84cc16",
  "#22c55e",
  "#10b981",
  "#14b8a6",
  "#06b6d4",
  "#0ea5e9",
  "#3b82f6",
  "#6366f1",
  "#8b5cf6",
  "#a855f7",
  "#d946ef",
  "#ec4899",
  "#f43f5e",
];

interface GradientStop {
  color: string;
  position: number;
}

interface ColorSelectorProps {
  /** Current color value (hex, rgba, gradient string, or "inherit") */
  value: string | null | undefined;

  /** Callback when color changes */
  onChange: (value: string) => void;

  /** Label for the input */
  label?: string;

  /** Description/hint text */
  description?: string;

  /** Error message */
  error?: string;

  /** Placeholder text */
  placeholder?: string;

  /** Disabled state */
  disabled?: boolean;

  /** Allow selecting "inherit" option */
  allowInherit?: boolean;

  /** Custom title for the modal */
  modalTitle?: string;
}

export const ColorSelector: React.FC<ColorSelectorProps> = ({
  value,
  onChange,
  label,
  description,
  error,
  placeholder = "Select color",
  disabled = false,
  allowInherit = false,
  modalTitle = "Select Color",
}) => {
  const [opened, setOpened] = useState(false);

  // Working state (changes here until Confirm is clicked)
  const [isInherited, setIsInherited] = useState(false);
  const [mode, setMode] = useState<"solid" | "gradient">("solid");
  const [solidColor, setSolidColor] = useState("#ffffff");
  const [alpha, setAlpha] = useState(1);
  const [gradientType, setGradientType] = useState<"linear" | "radial">(
    "linear"
  );
  const [gradientAngle, setGradientAngle] = useState(90);
  const [gradientStops, setGradientStops] = useState<GradientStop[]>([
    { color: "#ff0000", position: 0 },
    { color: "#0000ff", position: 100 },
  ]);

  // Editing gradient stop color
  const [editingStopIndex, setEditingStopIndex] = useState<number | null>(null);

  // Parse value and initialize working state when modal opens
  useEffect(() => {
    if (!opened) return;

    if (value === "inherit") {
      setIsInherited(true);
      return;
    }

    setIsInherited(false);

    if (!value) return;

    if (
      value.startsWith("linear-gradient") ||
      value.startsWith("radial-gradient")
    ) {
      setMode("gradient");

      // Parse gradient type
      if (value.startsWith("linear-gradient")) {
        setGradientType("linear");

        // Extract angle
        const angleMatch = value.match(/linear-gradient\((\d+)deg/);
        if (angleMatch) {
          setGradientAngle(parseInt(angleMatch[1]));
        }
      } else {
        setGradientType("radial");
      }

      // Parse color stops
      const stopsMatch = value.match(/#[0-9a-fA-F]{6,8}\s+\d+%/g);
      if (stopsMatch && stopsMatch.length > 0) {
        const stops = stopsMatch.map((stop) => {
          const [color, pos] = stop.split(/\s+/);
          return {
            color,
            position: parseInt(pos),
          };
        });
        setGradientStops(stops);
      }
    } else {
      setMode("solid");
      // Handle hex colors with or without alpha
      if (value.startsWith("#")) {
        if (value.length === 9) {
          // Has alpha channel
          const hexAlpha = value.substring(7, 9);
          const alphaValue = parseInt(hexAlpha, 16) / 255;
          setAlpha(alphaValue);
          setSolidColor(value.substring(0, 7));
        } else {
          setSolidColor(value);
          setAlpha(1);
        }
      }
    }
  }, [opened, value]);

  const buildColorValue = () => {
    if (isInherited) {
      return "inherit";
    }

    if (mode === "solid") {
      if (alpha < 1) {
        const alphaHex = Math.round(alpha * 255)
          .toString(16)
          .padStart(2, "0");
        return `${solidColor}${alphaHex}`;
      }
      return solidColor;
    } else {
      const stops = gradientStops
        .map((stop) => `${stop.color} ${stop.position}%`)
        .join(", ");

      if (gradientType === "linear") {
        return `linear-gradient(${gradientAngle}deg, ${stops})`;
      } else {
        return `radial-gradient(circle, ${stops})`;
      }
    }
  };

  const handleConfirm = () => {
    const newValue = buildColorValue();
    onChange(newValue);
    setOpened(false);
    setEditingStopIndex(null);
  };

  const handleCancel = () => {
    setOpened(false);
    setEditingStopIndex(null);
  };

  const updateGradientStop = (
    index: number,
    field: "color" | "position",
    value: string | number
  ) => {
    const newStops = [...gradientStops];
    newStops[index] = {
      ...newStops[index],
      [field]: value,
    };
    setGradientStops(newStops);
  };

  const addGradientStop = () => {
    if (gradientStops.length < 3) {
      setGradientStops([
        ...gradientStops,
        { color: getRandomHexColor(), position: 50 },
      ]);
    }
  };

  const removeGradientStop = (index: number) => {
    if (gradientStops.length > 2) {
      setGradientStops(gradientStops.filter((_, i) => i !== index));
      setEditingStopIndex(null);
    }
  };

  const handleStopColorChange = (color: string) => {
    if (editingStopIndex !== null) {
      updateGradientStop(editingStopIndex, "color", color);
    }
  };

  // Generate preview style for current working state
  const getPreviewStyle = () => {
    const currentValue = buildColorValue();

    if (
      currentValue.startsWith("linear-gradient") ||
      currentValue.startsWith("radial-gradient")
    ) {
      return { background: currentValue };
    } else {
      return { background: currentValue };
    }
  };

  // Get button preview
  const getButtonPreview = () => {
    if (value === "inherit") {
      return (
        <Box
          style={{
            width: 20,
            height: 20,
            borderRadius: 4,
            border: "1px solid var(--mantine-color-gray-4)",
            background:
              "repeating-linear-gradient(45deg, var(--mantine-color-gray-3), var(--mantine-color-gray-3) 2px, transparent 2px, transparent 4px)",
          }}
        />
      );
    }

    return (
      <Box
        style={{
          width: 20,
          height: 20,
          borderRadius: 4,
          border: "1px solid var(--mantine-color-gray-4)",
          background: value || "#ffffff",
        }}
      />
    );
  };

  return (
    <Stack gap="xs">
      {label && (
        <Text size="sm" fw={500}>
          {label}
        </Text>
      )}
      {description && (
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      )}

      <Button
        variant="default"
        onClick={() => setOpened(true)}
        disabled={disabled}
        fullWidth
        leftSection={getButtonPreview()}
      >
        {value === "inherit" ? "Inherited" : placeholder}
      </Button>

      <Modal
        opened={opened}
        onClose={handleCancel}
        title={modalTitle}
        centered
        size="sm"
      >
        <Stack gap="md">
          {allowInherit && (
            <Checkbox
              label="Inherit color from parent"
              checked={isInherited}
              onChange={(e) => setIsInherited(e.currentTarget.checked)}
            />
          )}

          {!isInherited && (
            <>
              <SegmentedControl
                value={mode}
                onChange={(val) => {
                  setMode(val as "solid" | "gradient");
                  setEditingStopIndex(null);
                }}
                data={[
                  { label: "Solid", value: "solid" },
                  { label: "Gradient", value: "gradient" },
                ]}
                fullWidth
              />

              {mode === "solid" ? (
                <>
                  <ColorPicker
                    value={solidColor}
                    onChange={setSolidColor}
                    format="hex"
                    fullWidth
                  />

                  <Group gap="xs" wrap="wrap">
                    {PRESET_COLORS.map((color) => (
                      <ColorSwatch
                        key={color}
                        color={color}
                        onClick={() => setSolidColor(color)}
                        style={{ cursor: "pointer" }}
                      />
                    ))}
                  </Group>

                  <TextInput
                    label="Hex Color"
                    value={solidColor}
                    onChange={(e) => setSolidColor(e.currentTarget.value)}
                    placeholder="#000000"
                  />

                  <Box>
                    <Text size="sm" mb="xs">
                      Opacity: {Math.round(alpha * 100)}%
                    </Text>
                    <Slider
                      value={alpha * 100}
                      onChange={(val) => setAlpha(val / 100)}
                      min={0}
                      max={100}
                      step={1}
                    />
                  </Box>
                </>
              ) : (
                <>
                  {editingStopIndex === null ? (
                    <>
                      <SegmentedControl
                        value={gradientType}
                        onChange={(val) =>
                          setGradientType(val as "linear" | "radial")
                        }
                        data={[
                          { value: "linear", label: "Linear Gradient" },
                          { value: "radial", label: "Radial Gradient" },
                        ]}
                      />

                      {gradientType === "linear" && (
                        <NumberInput
                          label="Angle"
                          value={gradientAngle}
                          onChange={(val) => setGradientAngle(Number(val))}
                          min={0}
                          max={360}
                          suffix="°"
                        />
                      )}

                      <Stack gap="sm">
                        <Group justify="space-between">
                          <Text size="sm" fw={500}>
                            Color Stops
                          </Text>
                          {gradientStops.length < 3 && (
                            <ActionIcon
                              size="sm"
                              variant="light"
                              onClick={addGradientStop}
                            >
                              <AppIcon iconName="Plus" size={14} />
                            </ActionIcon>
                          )}
                        </Group>

                        {gradientStops.map((stop, index) => (
                          <Group key={index} gap="xs">
                            <ActionIcon
                              size={24}
                              variant="default"
                              onClick={() => setEditingStopIndex(index)}
                              style={{
                                background: stop.color,
                                border: "1px solid var(--mantine-color-gray-4)",
                              }}
                            />
                            <TextInput
                              value={stop.color}
                              readOnly
                              onClick={() => setEditingStopIndex(index)}
                              placeholder="#000000"
                              style={{ flex: 1, cursor: "pointer" }}
                            />
                            <NumberInput
                              value={stop.position}
                              onChange={(val) =>
                                updateGradientStop(
                                  index,
                                  "position",
                                  Number(val)
                                )
                              }
                              min={0}
                              max={100}
                              suffix="%"
                              w={80}
                            />
                            {gradientStops.length > 2 && (
                              <ActionIcon
                                size="sm"
                                color="red"
                                variant="subtle"
                                onClick={() => removeGradientStop(index)}
                              >
                                <AppIcon iconName="X" size={14} />
                              </ActionIcon>
                            )}
                          </Group>
                        ))}
                      </Stack>

                      <Box
                        style={{
                          height: 40,
                          borderRadius: 4,
                          border: "1px solid var(--mantine-color-gray-4)",
                          ...getPreviewStyle(),
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Group justify="space-between">
                        <Text size="sm" fw={500}>
                          Edit Stop {editingStopIndex + 1} Color
                        </Text>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          onClick={() => setEditingStopIndex(null)}
                        >
                          <AppIcon iconName="ArrowLeft" size={14} />
                        </ActionIcon>
                      </Group>

                      <ColorPicker
                        value={gradientStops[editingStopIndex].color}
                        onChange={handleStopColorChange}
                        format="hex"
                        fullWidth
                      />

                      <Group gap="xs" wrap="wrap">
                        {PRESET_COLORS.map((color) => (
                          <ColorSwatch
                            key={color}
                            color={color}
                            onClick={() => handleStopColorChange(color)}
                            style={{ cursor: "pointer" }}
                          />
                        ))}
                      </Group>

                      <TextInput
                        label="Hex Color"
                        value={gradientStops[editingStopIndex].color}
                        onChange={(e) =>
                          handleStopColorChange(e.currentTarget.value)
                        }
                        placeholder="#000000"
                      />

                      <Button
                        fullWidth
                        onClick={() => setEditingStopIndex(null)}
                      >
                        Done
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          )}

          <Group justify="space-between" mt="md">
            <Button variant="subtle" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </Group>
        </Stack>
      </Modal>

      {error && (
        <Text size="xs" c="red">
          {error}
        </Text>
      )}
    </Stack>
  );
};
